"use client";

/**
 * useEndReached
 * @description 스크롤 상자 안의 보초 요소가 보이면 콜백을 부른다 — `FlatList.onEndReached` 의
 *   웹 대응. 콜백이 없으면 관찰하지 않는다.
 */
import { useEffect, useRef, type RefObject } from "react";

/** 보초가 이만큼 앞에서 보일 때부터 "끝" 으로 친다 — 카드 한 장 폭 정도 */
const ROOT_MARGIN = "0px 160px 0px 0px";

export function useEndReached<E extends HTMLElement>(
  rootRef: RefObject<E | null>,
  onEndReached?: () => void,
): RefObject<HTMLDivElement | null> {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onEndReached);
  callbackRef.current = onEndReached;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = rootRef.current;
    if (!sentinel || !root || !onEndReached) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting))
          callbackRef.current?.();
      },
      { root, rootMargin: ROOT_MARGIN },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [rootRef, onEndReached]);

  return sentinelRef;
}
